(function() {
  // Store native fetch before other scripts can override it
  const nativeFetch = window.fetch.bind(window);
  console.log('[DEBUG] Native fetch stored:', typeof nativeFetch);

  /**
   * Focus Trap Class for Accessibility
   * Traps keyboard focus within a modal/drawer when open
   */
  class FocusTrap {
    constructor(element) {
      this.element = element;
      this.focusableSelectors = 'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
      this.firstFocusableElement = null;
      this.lastFocusableElement = null;
      this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    }

    activate() {
      const focusableElements = this.element.querySelectorAll(this.focusableSelectors);
      if (focusableElements.length === 0) return;

      this.firstFocusableElement = focusableElements[0];
      this.lastFocusableElement = focusableElements[focusableElements.length - 1];

      // Listen on document level to catch all Tab events before browser processes them
      document.addEventListener('keydown', this.boundHandleKeyDown);

      // Focus first element after a short delay to ensure modal is visible
      setTimeout(() => {
        if (this.firstFocusableElement) {
          this.firstFocusableElement.focus();
        }
      }, 100);
    }

    deactivate() {
      document.removeEventListener('keydown', this.boundHandleKeyDown);
    }

    handleKeyDown(e) {
      if (e.key !== 'Tab') return;

      // Only trap focus if currently focused element is inside the trap container
      if (!this.element.contains(document.activeElement)) return;

      if (e.shiftKey) { // Shift + Tab (backwards)
        if (document.activeElement === this.firstFocusableElement) {
          e.preventDefault();
          if (this.lastFocusableElement) {
            this.lastFocusableElement.focus();
          }
        }
      } else { // Tab (forwards)
        if (document.activeElement === this.lastFocusableElement) {
          e.preventDefault();
          if (this.firstFocusableElement) {
            this.firstFocusableElement.focus();
          }
        }
      }
    }
  }

  /**
   * Unified Progress Tracker
   * Handles multiple milestones (shipping, gifts, etc.) in single UI
   */
  class UnifiedProgressTracker {
    constructor(settings) {
      this.settings = settings;
      this.milestones = (settings.milestones || [])
        .filter(m => m.enabled === true)
        .sort((a, b) => a.threshold - b.threshold);
      this.previousProgress = 0;

      // Add caching for performance (with improved cache key)
      this.cachedState = null;
      this.cachedCartTotal = null;
      this.cachedItemCount = null;
    }

    updateProgress(cartTotal, itemCount = 0, giftSuppressed = false) {
      if (this.milestones.length === 0) return null;

      // When an active offer (e.g. a bundle) suppresses the GWP, drop gift-type
      // milestones so the tracker doesn't advertise a gift that won't be added.
      // Also keeps state.completed gift-free, so handleMilestoneGifts adds nothing.
      const milestones = giftSuppressed
        ? this.milestones.filter(m => m.type !== 'gift')
        : this.milestones;

      // Return cached if unchanged (compare total, item count AND suppression)
      if (this.cachedCartTotal === cartTotal &&
          this.cachedItemCount === itemCount &&
          this.cachedGiftSuppressed === giftSuppressed &&
          this.cachedState) {
        return this.cachedState;
      }

      const state = {
        cartTotal: cartTotal,
        completed: [],
        next: null,
        progress: 0,
        message: '',
        color: '#000000',
        // the (possibly gift-filtered) milestones this render is based on, so the
        // icon row + message + progress bar all agree under suppression.
        activeMilestones: milestones
      };

      // Find completed and next milestone
      milestones.forEach(milestone => {
        if (cartTotal >= milestone.threshold) {
          state.completed.push(milestone);
        } else if (!state.next) {
          state.next = milestone;
        }
      });

      // Calculate progress
      if (state.next) {
        const previousThreshold = state.completed.length > 0
          ? state.completed[state.completed.length - 1].threshold
          : 0;

        const range = state.next.threshold - previousThreshold;
        const progress = cartTotal - previousThreshold;
        state.progress = Math.min(100, Math.max(0, (progress / range) * 100));
        state.color = state.next.color;

        // Build message
        state.message = this.buildMessage(state, cartTotal, milestones);

      } else if (state.completed.length > 0) {
        // All completed
        state.progress = 100;
        state.color = '#108043';

        const rewards = state.completed
          .map(m => m.qualifiedMessage.replace('✓ ', '').replace(/!+$/, ''))
          .join(' + ');
        state.message = `🎉 ${rewards}!`;
      }

      // Cache results for performance (with item count + suppression in cache key)
      this.cachedCartTotal = cartTotal;
      this.cachedItemCount = itemCount;
      this.cachedGiftSuppressed = giftSuppressed;
      this.cachedState = state;

      this.renderUI(state);
      return state;
    }

    buildMessage(state, cartTotal, milestones = this.milestones) {
      const next = state.next;
      const completed = state.completed;

      // All completed
      if (!next && completed.length > 0) {
        const rewards = completed
          .map(m => m.qualifiedMessage.replace('✓ ', '').replace(/!+$/, ''))
          .join(' + ');
        return `🎉 ${rewards}!`;
      }

      // At this point, next must exist
      const remaining = next.threshold - cartTotal;
      const amountText = this.formatMoney(remaining);

      // No milestones completed - check for combined messaging
      if (completed.length === 0) {
        // EQUAL-threshold milestones (e.g. free shipping AND the offer's gift both
        // at $100) are ONE moment for the shopper — say both promises, or the
        // second reward is invisible until it's suddenly earned.
        const sameThreshold = milestones.filter(m =>
          m !== next && m.enabled !== false && m.threshold === next.threshold && m.shortName
        );
        if (sameThreshold.length) {
          const extras = sameThreshold.map(m => String(m.shortName).toLowerCase()).join(' + ');
          return `${next.progressMessage.replace('{amount}', amountText)} + ${extras}`;
        }

        // Check if approaching another milestone
        const combineThreshold = this.settings.combineThreshold || 1000;
        const nextAfter = milestones.find(m =>
          m.threshold > next.threshold &&
          (m.threshold - cartTotal) <= (combineThreshold * 2)
        );

        // Within combine threshold of first milestone - show both
        if (remaining < combineThreshold && nextAfter) {
          const amount2 = this.formatMoney(nextAfter.threshold - cartTotal);
          return `Spend ${amountText} for ${next.shortName} + ${amount2} for ${nextAfter.shortName}`;
        }

        // Just show next
        return next.progressMessage.replace('{amount}', amountText);
      }

      // Some completed, show next
      const completedText = completed
        .map(m => m.qualifiedMessage.replace('✓ ', '').replace(/!+$/, ''))
        .join(' + ');

      return `✓ ${completedText}! Spend ${amountText} for ${next.shortName.toLowerCase()}`;
    }

    renderUI(state) {
      const tracker = document.querySelector('[data-milestone-tracker]');
      if (!tracker) return;

      const message = tracker.querySelector('[data-milestone-message]');
      const progressFill = tracker.querySelector('[data-milestone-progress]');
      const icons = tracker.querySelector('[data-milestone-icons]');

      // Update message
      if (message) {
        message.textContent = state.message;
      }

      // Update progress bar
      if (progressFill) {
        progressFill.style.width = state.progress + '%';
        progressFill.style.backgroundColor = state.color;
      }

      // Update ARIA attributes
      const progressBar = document.querySelector('.cart-drawer__milestone-progress');
      if (progressBar) {
        progressBar.setAttribute('aria-valuenow', Math.round(state.progress));
      }

      // Update icons
      if (icons) {
        this.renderIcons(icons, state);
      }

      // Update tracker state
      tracker.classList.toggle('is-completed', state.progress === 100);

      // Celebration animation and screen reader announcement
      if (state.progress === 100 && this.previousProgress < 100) {
        tracker.classList.add('is-celebrating');
        setTimeout(() => tracker.classList.remove('is-celebrating'), 600);
        this.announceToScreenReader(state.message);

        // Track milestone achievement
        if (window.analytics && typeof window.analytics.track === 'function') {
          try {
            const milestone = state.next || (state.completed.length > 0 ? state.completed[state.completed.length - 1] : null);
            window.analytics.track('Milestone Achieved', {
              milestone_id: milestone ? milestone.id : 'all',
              milestone_type: milestone ? milestone.type : 'multiple',
              cart_value: state.cartTotal / 100,
              threshold: milestone ? milestone.threshold / 100 : null
            });
          } catch (error) {
            console.warn('Analytics tracking failed:', error);
          }
        }
      }

      this.previousProgress = state.progress;
    }

    renderIcons(container, state) {
      container.innerHTML = '';

      (state.activeMilestones || this.milestones).forEach(milestone => {
        const icon = document.createElement('span');
        icon.className = 'milestone-icon';
        icon.textContent = milestone.icon;
        icon.title = `${milestone.shortName} at ${this.formatMoney(milestone.threshold)}`;

        if (state.completed.some(m => m.id === milestone.id)) {
          icon.classList.add('milestone-icon--completed');
        } else if (state.next && state.next.id === milestone.id) {
          icon.classList.add('milestone-icon--active');
        } else {
          icon.classList.add('milestone-icon--pending');
        }

        container.appendChild(icon);
      });
    }

    announceToScreenReader(message) {
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'assertive');
      announcement.className = 'sr-only';
      announcement.textContent = message;

      document.body.appendChild(announcement);

      setTimeout(() => announcement.remove(), 1000);
    }

    formatMoney(cents) {
      if (window.theme && window.theme.Shopify && window.theme.Shopify.formatMoney) {
        return window.theme.Shopify.formatMoney(cents, window.theme.money_format);
      }
      return '$' + (cents / 100).toFixed(2);
    }
  }

  // Exposed for behavioral tests (tests/gift-blackout.spec.js) — lets a spec
  // construct a tracker with known milestones and assert the gift-suppression
  // affects the MESSAGE, not just the gift item.
  if (typeof window !== 'undefined') window.UnifiedProgressTracker = UnifiedProgressTracker;

  class CartDrawer {
    constructor() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      if (!this.drawer) return;

      this.overlay = this.drawer.querySelector('[data-cart-drawer-overlay]');
      this.closeButton = this.drawer.querySelector('[data-cart-drawer-close]');
      this.content = this.drawer.querySelector('[data-cart-drawer-content]');
      this.itemsContainer = this.drawer.querySelector('[data-cart-items]');
      this.emptyState = this.drawer.querySelector('[data-cart-empty]');
      this.footer = this.drawer.querySelector('[data-cart-footer]');
      this.subtotal = this.drawer.querySelector('[data-cart-subtotal]');
      this.loading = this.drawer.querySelector('[data-cart-loading]');
      this.itemTemplate = document.querySelector('[data-cart-item-template]');
      this.shippingThreshold = this.drawer.querySelector('[data-shipping-threshold]');
      this.shippingMessage = this.drawer.querySelector('[data-shipping-message]');
      this.shippingProgress = this.drawer.querySelector('[data-shipping-progress]');

      this.isOpen = false;
      this.isUpdating = false;
      this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      this.focusTrap = null;
      this.previouslyFocusedElement = null;

      // Use native fetch to avoid third-party wrappers
      this.fetch = nativeFetch;

      // Initialize milestone tracker if enabled
      this.milestoneTracker = null;
      this.milestoneDebounce = null;
      if (window.cartDrawerSettings?.progressTracker?.enabled) {
        this.milestoneTracker = new UnifiedProgressTracker(
          window.cartDrawerSettings.progressTracker
        );
      }

      // Guard flag for gift handling to prevent race conditions
      this.isHandlingGifts = false;

      this.init();
    }

    init() {
      console.log('[DEBUG] CartDrawer initializing...');

      // Close button event
      if (this.closeButton) {
        this.closeButton.addEventListener('click', () => this.close());
      }

      // Overlay close event
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // Escape key closes drawer (accessibility)
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      // Listen for cart events
      document.addEventListener('cart:open', () => this.open());
      document.addEventListener('cart:refresh', () => this.refresh());
      document.addEventListener('cart:add', (e) => this.handleAddToCart(e.detail));

      // Listen for cart icon clicks - but not on cart page
      if (!window.location.pathname.includes('/cart')) {
        document.querySelectorAll('[data-cart-toggle]').forEach(trigger => {
          trigger.addEventListener('click', (e) => {
            e.preventDefault(); // Prevent any default action
            this.open();
          });
        });
      }

      // Intercept add to cart forms
      this.interceptAddToCartForms();
      console.log('[DEBUG] Form interceptor set up');

      // Disable old cart popup
      this.disableOldCartPopup();

      // Setup upsell add to cart functionality
      this.setupUpsellAddToCart();

      // Setup note toggle
      this.setupNoteToggle();

      // Setup checkout button handler
      this.setupCheckoutButton();

      console.log('[DEBUG] CartDrawer initialization complete');
    }

    open() {
      if (this.isOpen) return;

      // Save the currently focused element to restore later
      this.previouslyFocusedElement = document.activeElement;

      this.isOpen = true;
      this.drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      // Activate focus trap
      const panel = this.drawer.querySelector('.cart-drawer__panel');
      if (panel) {
        this.focusTrap = new FocusTrap(panel);
        this.focusTrap.activate();
      }

      // Only refresh if we don't have current cart data, otherwise just update what we have
      if (!this.lastCartData) {
        this.refresh();
      } else {
        this.updateDrawer(this.lastCartData);
      }
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.drawer.classList.remove('is-open');
      document.body.style.overflow = '';

      // Deactivate focus trap
      if (this.focusTrap) {
        this.focusTrap.deactivate();
        this.focusTrap = null;
      }

      // Restore focus to the element that opened the drawer
      if (this.previouslyFocusedElement && typeof this.previouslyFocusedElement.focus === 'function') {
        setTimeout(() => {
          this.previouslyFocusedElement.focus();
        }, 100);
      }
    }

    async refresh() {
      if (this.isUpdating) return;

      this.showLoading(true);

      try {
        const response = await this.fetch('/cart.js');
        const cart = await response.json();
        this.lastCartData = cart; // Store cart data
        this.updateDrawer(cart);
      } catch (error) {
        console.error('Error fetching cart:', error);
      } finally {
        this.showLoading(false);
      }
    }

    updateDrawer(cart) {
      // Clear current items
      this.itemsContainer.innerHTML = '';

      if (cart.item_count === 0) {
        this.emptyState.style.display = 'block';
        this.footer.style.display = 'none';
      } else {
        this.emptyState.style.display = 'none';
        this.footer.style.display = 'block';

        // Set lines render as grouped blocks — a set is an entity, not unrelated
        // lines. ONE GROUP PER SET (sweep H1): preconfig sets group by their
        // exact stamp; everything else that BELONGS to the generic set offer
        // (builder-stamped OR collection-eligible — Sean's unified model,
        // 2026-08-12: PDP/recs adds join the set the money already pools) forms
        // the builder group. Gifts and non-eligible items render normally.
        const preStamps = {};
        (window.OfferRegistry || []).forEach((o) => {
          const t = o.trigger || {};
          if (t.type === 'line_property' && t.key === '_bundle' && t.value != null) preStamps[String(t.value)] = 1;
        });
        const genericOffer = this.builderOffer();
        const eligibleIds = {};
        if (genericOffer && genericOffer.trigger && genericOffer.trigger.type === 'collection_qty') {
          (genericOffer.trigger.productIds || []).forEach((id) => { eligibleIds[String(id)] = 1; });
        }
        const isGift = (i) => (i.properties || {})._offer_gift || (i.properties || {})._gift_item === 'true';
        const inBuilderSet = (i) => {
          if (isGift(i)) return false;
          const v = (i.properties || {})._bundle;
          if (v && preStamps[String(v)]) return false;
          return !!v || !!eligibleIds[String(i.product_id)];
        };
        const preItems = cart.items.filter(i => !isGift(i) && i.properties && i.properties._bundle && preStamps[String(i.properties._bundle)]);
        const builderItems = cart.items.filter(inBuilderSet);
        const rest = cart.items.filter(i => !inBuilderSet(i) && preItems.indexOf(i) === -1);
        if (preItems.length) {
          const byStamp = new Map();
          preItems.forEach((i) => {
            const s = i.properties._bundle;
            if (!byStamp.has(s)) byStamp.set(s, []);
            byStamp.get(s).push(i);
          });
          byStamp.forEach((lines) => this.itemsContainer.appendChild(this.buildSetGroup(lines, cart)));
        }
        if (builderItems.length) {
          this.itemsContainer.appendChild(this.buildSetGroup(builderItems, cart));
        }
        rest.forEach(item => {
          this.itemsContainer.appendChild(this.createItemElement(item));
        });

        // Update subtotal and total
        this.subtotal.textContent = this.formatMoney(cart.total_price);
        const totalElement = this.drawer.querySelector('[data-cart-total]');
        if (totalElement) {
          totalElement.textContent = this.formatMoney(cart.total_price);
        }
      }

      // Republish the resolved offer for THIS cart (window.__activeOffer +
      // `offer:resolved` event) so every surface — the drawer, the bundle page,
      // the Alia popup — shows the same copy. A bundle in cart flips the resolved
      // offer from the default to the bundle offer; an empty cart flips it back.
      this.publishActiveOffer(cart);

      // ONE orchestrated write pass (discount -> gift -> attribute): sequential,
      // response-verified, at most one cart:refresh, breaker-capped on repeated
      // failure (2026-08-11 sweep — replaces three fire-and-forget writers).
      // Skipped when the collection-quantity offer owns the drawer (exclusivity —
      // its engine must not fight the offer system's writers).
      if (
        window.OfferReward &&
        typeof window.OfferReward.syncAll === 'function' &&
        !window.cartDrawerSettings?.collectionOffer?.enabled
      ) {
        window.OfferReward.syncAll(cart);
      }
      // Savings line ("Set discount  -$56") from the REAL applied discount.
      this.renderOfferSavings(cart);

      // Always update milestone progress (for both empty and filled carts)
      this.updateMilestoneProgress(cart);

      // Update bundle discount message
      this.updateBundleMessage(cart);

      // OPTION 1 (Sean + Henya, 2026-08-12): set in cart -> recs step aside for
      // one "Add to your set" builder link; no set -> recs + a quiet builder
      // pitch. AFTER updateBundleMessage so this surface owns the final state.
      this.updateBuilderLink(cart);

      // Update cart count in header
      this.updateCartCount(cart.item_count);
      
      // Update payment installments (async)
      this.updateInstallments(cart);
    }

    createItemElement(item) {
      const template = this.itemTemplate.content.cloneNode(true);
      const element = template.querySelector('[data-cart-item]');

      // Set variant ID
      element.dataset.variantId = item.variant_id;

      // Image
      const image = element.querySelector('[data-item-image]');
      if (image && item.image) {
        image.src = item.image;
        image.alt = item.product_title;
      }

      // Check if this is a gift item (check early to use throughout)
      const isGiftItem = item.properties && item.properties._gift_item === 'true';

      // (Bundle lines are grouped by buildSetGroup — no per-line badge needed.)

      // URLs
      element.querySelectorAll('[data-item-url]').forEach(link => {
        if (isGiftItem) {
          // Disable links for gift items
          link.href = '#';
          link.style.pointerEvents = 'none';
          link.style.cursor = 'default';
          link.addEventListener('click', (e) => {
            e.preventDefault();
            return false;
          });
        } else {
          link.href = item.url;
        }
      });

      // Title
      const title = element.querySelector('[data-item-title]');
      if (title) {
        title.textContent = item.product_title;
      }

      // Variant
      const variant = element.querySelector('[data-item-variant]');
      if (variant && item.variant_title && item.variant_title !== 'Default Title') {
        variant.textContent = item.variant_title;
      }

      // Price
      const price = element.querySelector('[data-item-price]');
      if (price) {
        price.textContent = this.formatMoney(item.final_price);
      }

      // Original price (if on sale)
      const originalPrice = element.querySelector('[data-item-original-price]');
      if (originalPrice && item.original_price > item.final_price) {
        originalPrice.textContent = this.formatMoney(item.original_price);
        originalPrice.style.display = 'inline';
      }

      // Hide entire quantity container for gift items
      const quantityContainer = element.querySelector('.cart-drawer__item-quantity');
      if (isGiftItem && quantityContainer) {
        quantityContainer.style.display = 'none';
      }

      // Quantity
      const quantityInput = element.querySelector('[data-quantity-input]');
      if (quantityInput) {
        quantityInput.value = item.quantity;
        quantityInput.dataset.variantId = item.variant_id;

        if (!isGiftItem) {
          quantityInput.addEventListener('change', (e) => {
            this.updateQuantity(item.variant_id, parseInt(e.target.value));
          });
        }
      }

      // Quantity buttons with mobile-friendly events
      const decreaseBtn = element.querySelector('[data-quantity-decrease]');
      if (decreaseBtn && !isGiftItem) {
        this.addMobileFriendlyListener(decreaseBtn, () => {
          this.updateQuantity(item.variant_id, Math.max(0, item.quantity - 1));
        });
      }

      const increaseBtn = element.querySelector('[data-quantity-increase]');
      if (increaseBtn && !isGiftItem) {
        this.addMobileFriendlyListener(increaseBtn, () => {
          this.updateQuantity(item.variant_id, item.quantity + 1);
        });
      }

      // Remove button with mobile-friendly events
      const removeBtn = element.querySelector('[data-item-remove]');
      if (removeBtn) {
        this.addMobileFriendlyListener(removeBtn, () => {
          this.updateQuantity(item.variant_id, 0);
        });
      }

      // Check if this is a gift item
      if (item.properties && item.properties._gift_item === 'true') {
        // Add visual badge
        const titleElement = element.querySelector('[data-item-title]');
        if (titleElement) {
          const badge = document.createElement('span');
          badge.className = 'cart-drawer__gift-badge';
          badge.textContent = '🎁 Free Gift';
          titleElement.appendChild(badge);
        }

        // Lock quantity to 1
        const quantityInput = element.querySelector('[data-quantity-input]');
        if (quantityInput) {
          quantityInput.disabled = true;
          quantityInput.value = 1;
        }

        // Hide quantity controls
        const decreaseBtn = element.querySelector('[data-quantity-decrease]');
        const increaseBtn = element.querySelector('[data-quantity-increase]');
        if (decreaseBtn) decreaseBtn.style.display = 'none';
        if (increaseBtn) increaseBtn.style.display = 'none';

        // Hide remove button
        const removeBtn = element.querySelector('[data-item-remove]');
        if (removeBtn) {
          removeBtn.style.display = 'none';

          // Add info text
          const infoText = document.createElement('div');
          infoText.className = 'cart-drawer__gift-info';
          infoText.textContent = 'Automatically added with your order';
          removeBtn.parentNode.appendChild(infoText);
        }

        // Mark element as gift
        element.dataset.isGift = 'true';
      }

      return element;
    }

    async updateQuantity(variantId, quantity) {
      if (this.isUpdating) return;
      
      this.isUpdating = true;
      this.showLoading(true);

      try {
        const response = await this.fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId.toString(),
            quantity: quantity
          })
        });

        const cart = await response.json();
        this.lastCartData = cart; // Store updated cart data
        this.updateDrawer(cart);

        // Dispatch event for other components
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      } catch (error) {
        console.error('Error updating quantity:', error);
      } finally {
        this.isUpdating = false;
        this.showLoading(false);
      }
    }

    async handleAddToCart(data) {
      this.open();
      await this.refresh();
    }

    interceptAddToCartForms() {
      // Intercept product form submissions
      document.addEventListener('submit', async (e) => {
        const form = e.target;

        // Check if this is an add to cart form
        if (!form.action || !form.action.includes('/cart/add')) return;

        console.log('[DEBUG] Form submit intercepted:', form);
        e.preventDefault();
        console.log('[DEBUG] Default prevented:', e.defaultPrevented);

        const formData = new FormData(form);
        const data = {};

        // Convert FormData to object
        for (const [key, value] of formData.entries()) {
          data[key] = value;
        }

        console.log('[DEBUG] Form data:', data);

        try {
          console.log('[DEBUG] Calling fetch to /cart/add.js');
          const response = await this.fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [{
                id: data.id,
                quantity: parseInt(data.quantity || 1)
              }]
            })
          });

          console.log('[DEBUG] Fetch response received:', response);
          console.log('[DEBUG] Response.ok:', response ? response.ok : 'response is undefined!');

          if (response && response.ok) {
            console.log('[DEBUG] Response is OK, parsing JSON');
            const result = await response.json();
            console.log('[DEBUG] Parsed result:', result);
            document.dispatchEvent(new CustomEvent('cart:add', { detail: result }));
          } else {
            console.error('[DEBUG] Response not OK or undefined. Response:', response);
          }
        } catch (error) {
          console.error('[DEBUG] Error adding to cart:', error);
          console.error('[DEBUG] Error stack:', error.stack);
        }
      });

      // Intercept AJAX add to cart buttons
      document.addEventListener('click', async (e) => {
        const button = e.target.closest('[data-add-to-cart]');
        if (!button) return;

        console.log('[DEBUG] Button click intercepted:', button);
        e.preventDefault();

        const variantId = button.dataset.variantId || button.dataset.variant_id;
        const quantity = parseInt(button.dataset.quantity || 1);

        console.log('[DEBUG] Button variant ID:', variantId, 'quantity:', quantity);

        if (!variantId) {
          console.error('[DEBUG] No variant ID found on button');
          return;
        }

        try {
          console.log('[DEBUG] Calling fetch to /cart/add.js (button click)');
          const response = await this.fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [{
                id: variantId,
                quantity: quantity
              }]
            })
          });

          console.log('[DEBUG] Button fetch response received:', response);

          if (response && response.ok) {
            console.log('[DEBUG] Button response is OK');
            const result = await response.json();
            console.log('[DEBUG] Button parsed result:', result);
            document.dispatchEvent(new CustomEvent('cart:add', { detail: result }));
          } else {
            console.error('[DEBUG] Button response not OK or undefined. Response:', response);
          }
        } catch (error) {
          console.error('[DEBUG] Error adding to cart (button):', error);
          console.error('[DEBUG] Error stack:', error.stack);
        }
      });
    }

    showLoading(show) {
      if (this.loading) {
        if (show) {
          this.loading.classList.add('is-loading');
        } else {
          this.loading.classList.remove('is-loading');
        }
      }
    }

    disableOldCartPopup() {
      // Override showThemeModal to prevent old popup
      if (window.showThemeModal) {
        const originalShowThemeModal = window.showThemeModal;
        window.showThemeModal = function(modalHtml) {
          // If this is the cart popup, don't show it
          if (modalHtml && modalHtml.includes('added-to-cart')) {
            // Instead, trigger our drawer
            document.dispatchEvent(new Event('cart:open'));
            return;
          }
          // Otherwise, allow other modals
          return originalShowThemeModal(modalHtml);
        };
      }
      
      // Remove feedback classes that trigger old popup
      document.querySelectorAll('.feedback-add_in_modal, .feedback-add_in_modal_no_checkout').forEach(el => {
        el.classList.remove('feedback-add_in_modal', 'feedback-add_in_modal_no_checkout');
        el.classList.add('feedback-drawer');
      });
    }

    formatMoney(cents) {
      // Use theme money format if available
      if (window.theme && window.theme.Shopify && window.theme.Shopify.formatMoney) {
        return window.theme.Shopify.formatMoney(cents, window.theme.money_format_with_code_preference || window.theme.money_format);
      }
      
      // Fallback to basic formatting
      return '$' + (cents / 100).toFixed(2);
    }

    updateCartCount(count) {
      // Update all cart count indicators with data attribute
      document.querySelectorAll('[data-cart-count]').forEach(element => {
        element.textContent = count;
      });

      // Update cart icon badges with class
      document.querySelectorAll('.cart-count, .cart-badge').forEach(element => {
        element.textContent = count;
        if (count > 0) {
          element.style.display = '';
        } else {
          element.style.display = 'none';
        }
      });

      // Update cart drawer title
      const cartTitle = this.drawer.querySelector('[data-cart-title]');
      if (cartTitle) {
        const itemText = count === 1 ? 'item' : 'items';
        cartTitle.textContent = `Your Bag (${count} ${itemText})`;
      }

      // Update any text that includes cart count in parentheses (like in header)
      document.querySelectorAll('.text-link').forEach(element => {
        const text = element.textContent;
        if (text.includes('Cart') || text.includes('cart')) {
          // Remove existing count in parentheses and add new one
          const baseText = text.replace(/\s*\(\d+\)$/, '');
          element.textContent = count > 0 ? `${baseText} (${count})` : baseText;
        }
      });
    }
    
    /**
     * Derive the tracker's milestone list from BASELINE + THE RESOLVED OFFER
     * (docs/tracker-offer-migration-spec.md — Sean-approved):
     *   - shipping milestones: unchanged, from milestone_shipping_* settings
     *   - gift milestone: synthesized from window.__activeOffer when its reward is
     *     a GWP — threshold = reward.min_subtotal, copy = the offer's copy.locked/
     *     unlocked ({amount} substitution supported). The tracker then always tells
     *     the story of the offer actually in force (same signal as every surface).
     *   - bundle/discount/message offers: NO gift milestone (shipping only — the
     *     set group + savings line carry that story).
     * No offer system present -> the configured list, byte-for-byte (back-compat).
     */
    deriveMilestones() {
      const cfg = (window.cartDrawerSettings?.progressTracker?.milestones) || [];
      if (!window.__activeOffer && !window.__sessionOffer) return cfg;
      const derived = cfg.filter((m) => m.type === 'shipping'); // the offer system owns gifts
      // The gift milestone derives from the SITEWIDE SESSION layer (Sean's model):
      // it shows alongside a builder set (stacking), and disappears when a blackout
      // landing replaced the sitewide offer or the campaign suppresses it — the
      // exact same gate the applicator uses (OfferReward.giftOffer), so the tracker
      // can never promise a gift syncGift won't add.
      const offer = (window.OfferReward && window.OfferReward.giftOffer && window.OfferReward.giftOffer()) || null;
      if (!offer) return derived;
      const r = offer.reward || {};
      if ((r.type === 'gwp_free_variant' || r.type === 'gwp_coded') && r.variant_id) {
        const c = offer.copy || {};
        // EFFECTIVE threshold floors at 1 cent: a zero-threshold ("every order")
        // gift must show its PROMISE on an EMPTY cart, not "is in the cart!" —
        // the applicator refuses to gift an empty cart (has-purchase guard), and
        // the tracker must never claim what syncGift won't add (live-caught,
        // 2026-08-11: empty cart said the Allium was already in it).
        const threshold = Math.max(r.min_subtotal || 0, 1);
        // {threshold} in offer copy renders the ACTUAL gate (reward.min_subtotal) —
        // copy authors must never hardcode the dollar amount in prose, or the
        // message and the gate drift apart. ({amount} = remaining, substituted
        // later by the tracker.)
        const sub = (s) => (s || '').replace(/\{threshold\}/g, this.formatMoney(threshold));
        derived.push({
          id: 'offer_gift_' + offer.id,
          type: 'gift',
          threshold: threshold,
          priority: 2,
          icon: '🎁',
          progressMessage: sub(c.locked) || 'Spend {amount} for a free gift',
          qualifiedMessage: sub(c.unlocked) || 'Free gift unlocked',
          shortName: 'a free gift', // composes into combined messages ("…free shipping + a free gift")
          color: '#5a6b47',
          enabled: true,
          // ADDED by OfferReward.syncGift — the legacy milestone-gift engine must
          // never act on this milestone (it has no product/variant settings).
          isOfferGift: true,
        });
      }
      return derived;
    }

    updateMilestoneProgress(cart) {
      // MULTI-OFFER: when a collection-quantity offer is active it OWNS the tracker,
      // and the money-milestone path below is skipped entirely (exclusive by
      // construction — no two elements fighting for the same DOM, no gift auto-add).
      // When the offer is disabled this branch never runs, so the money path is
      // byte-for-byte unchanged. See docs/multi-offer-next-steps.md.
      if (window.cartDrawerSettings?.collectionOffer?.enabled) {
        this.updateCollectionOffer(cart);
        return;
      }

      if (!window.cartDrawerSettings?.progressTracker?.enabled) return;

      // Rebuild the tracker when the DERIVED milestone list changes (offer flip,
      // registry change) — a fresh instance means fresh cache: no stale offer copy.
      const derived = this.deriveMilestones();
      const derivedKey = JSON.stringify(derived.map((m) => `${m.id}:${m.threshold}:${m.progressMessage}`));
      if (derivedKey !== this._milestoneKey || !this.milestoneTracker) {
        this._milestoneKey = derivedKey;
        const prev = this.milestoneTracker ? this.milestoneTracker.previousProgress : 0;
        this.milestoneTracker = new UnifiedProgressTracker({
          ...window.cartDrawerSettings.progressTracker,
          milestones: derived,
        });
        // Carry progress across the rebuild — else an offer flip at 100% re-fires
        // the celebration animation + the Milestone Achieved analytics event.
        this.milestoneTracker.previousProgress = prev;
      }

      // Update UI immediately (no debounce for visual feedback)
      const cartTotal = cart.total_price;
      // Bundle (or any offer) suppressing the GWP also hides the gift MESSAGE, not
      // just the gift item — so the tracker never advertises a gift that won't add.
      const giftSuppressed = this.isGiftSuppressed(cart);
      const state = this.milestoneTracker.updateProgress(cartTotal, cart.item_count, giftSuppressed);

      // Show/hide tracker
      const tracker = document.querySelector('[data-milestone-tracker]');
      if (tracker) {
        tracker.style.display = state && state.message ? 'block' : 'none';
      }

      // Debounce business logic (gift handling) separately
      clearTimeout(this.milestoneDebounce);
      this.milestoneDebounce = setTimeout(async () => {
        // Handle gift milestones with proper async/await
        if (state && state.completed) {
          await this.handleMilestoneGifts(cart, state);
        }
      }, 300); // Increased from 150ms to standard 300ms
    }

    /**
     * Collection-quantity offer (DISPLAY-ONLY). Counts eligible items in the cart
     * and renders a locked/unlocked message + progress into the milestone tracker.
     * Never mutates the cart: no line item, no discount code, no checkout slot.
     *
     * MULTI-OFFER: first brick of the configurable multi-offer system. Config comes
     * from window.cartDrawerSettings.collectionOffer (offer-shaped: {trigger, delivery,
     * copy, shipping}). Future: that object is resolved from an 'Offer' metaobject via
     * the active-offer-context chokepoint, with suppression from the exclusivity matrix.
     * See docs/multi-offer-next-steps.md.
     */
    updateCollectionOffer(cart) {
      const offer = window.cartDrawerSettings?.collectionOffer;
      const tracker = document.querySelector('[data-milestone-tracker]');
      if (!offer || !tracker) return;

      const minQty = offer.trigger.minQty || 2;
      const count = this.countCollectionItems(cart, offer.trigger.productIds);
      const qualified = count >= minQty;
      const remaining = Math.max(0, minQty - count);

      // Main line: locked (with {remaining}) or unlocked
      let mainMessage;
      if (qualified) {
        mainMessage = offer.copy.unlocked;
      } else {
        const noun = offer.copy.itemNoun || 'item';
        const unit = remaining === 1 ? noun : noun + 's';
        mainMessage = (offer.copy.locked || 'Add {remaining}')
          .replace('{remaining}', remaining + ' ' + unit);
      }

      // Free-shipping note — SINGLE-SOURCED from milestone_shipping_* (via offer.shipping).
      // Shown as a secondary line in ALL states (not gated on locked), never its own bar.
      let shipNote = '';
      const ship = offer.shipping;
      if (ship && ship.noteEnabled) {
        if (cart.total_price >= ship.threshold) {
          // Normalize to exactly one leading checkmark regardless of the shared
          // setting's convention (the money tracker strips a leading "✓ " too).
          const qn = (ship.qualifiedNote || 'Free shipping').replace('✓ ', '');
          shipNote = '✓ ' + qn;
        } else {
          const amount = this.formatMoney((ship.threshold || 0) - cart.total_price);
          shipNote = (ship.progressNote || 'Spend {amount} for free shipping')
            .replace('{amount}', amount);
        }
      }

      // Render: main line + optional shipping subline. Clear first every call so no
      // stale nodes survive a state change.
      const msgEl = tracker.querySelector('[data-milestone-message]');
      const fillEl = tracker.querySelector('[data-milestone-progress]');
      const progressPct = Math.min(100, (count / minQty) * 100);
      if (msgEl) {
        msgEl.textContent = '';
        const main = document.createElement('div');
        main.textContent = mainMessage;
        msgEl.appendChild(main);
        if (shipNote) {
          const note = document.createElement('div');
          note.className = 'cart-drawer__offer-shipnote';
          note.textContent = shipNote;
          msgEl.appendChild(note);
        }
      }
      if (fillEl) {
        fillEl.style.width = progressPct + '%';
        fillEl.style.backgroundColor = qualified ? '#108043' : '#000';
      }
      // Keep the progressbar ARIA in sync (the money path does this in renderUI).
      const progressBar = tracker.querySelector('.cart-drawer__milestone-progress');
      if (progressBar) progressBar.setAttribute('aria-valuenow', Math.round(progressPct));
      tracker.classList.toggle('is-completed', qualified);
      tracker.style.display = 'block';
    }

    /**
     * Count cart items whose product is in the eligible collection.
     * MULTI-OFFER: cart.js carries no collection membership, so we match against
     * the product IDs rendered from Liquid. (Liquid caps the collection loop; the
     * curated full-price collection is well under the cap — productCount is passed
     * for a future truncation warning.)
     */
    countCollectionItems(cart, productIds) {
      if (!Array.isArray(productIds) || productIds.length === 0) return 0;
      const ids = new Set(productIds);
      return (cart.items || []).reduce(
        (n, item) => (ids.has(item.product_id) ? n + item.quantity : n),
        0
      );
    }

    /**
     * Blackout check: is the milestone gift suppressed by an active offer (e.g. a
     * bundle)? Reads the offer registry (window.cartDrawerSettings.offerRegistry)
     * through the pure OfferResolver. No registry ⇒ false ⇒ behavior unchanged.
     */
    isGiftSuppressed(cart) {
      var reg = window.OfferRegistry || [];
      if (!window.OfferResolver || !reg.length) return false;
      var r = window.OfferResolver.resolve(cart, reg);
      return !!(r.suppressed && r.suppressed.indexOf('milestone-gift') !== -1);
    }

    /**
     * The grouped SET block: header (count + tier status), the set lines nested,
     * footer (next-tier nudge + Remove set). The set is one entity — this is the
     * "part of a bundle" indication, and the honest surface for tier changes
     * (edit the set below tier -> "no set discount" + prices revert, because
     * OfferReward.syncCartDiscount re-arms/clears the tier code on every change).
     * Tier data + copy come from the offer registry (metaobject), not hardcode.
     */
    buildSetGroup(setItems, cart) {
      const qty = setItems.reduce((n, i) => n + (i.quantity || 1), 0);
      // The group renders the offer THE LINES BELONG TO, derived from their own
      // _bundle stamp: exact value match first (a preconfig bundle), else the
      // GENERIC builder offer (no trigger.value). Never a blind first-match scan —
      // the registry holds multiple _bundle offers (builder + fixed bundles).
      const stamp = (setItems[0] && setItems[0].properties && setItems[0].properties._bundle) || null;
      // Window-filter like the resolver does (sweep C1): an EXPIRED preconfig
      // offer must not lend its percent/code to a group after its window closes.
      const inWin = (o) => !(window.OfferResolver && window.OfferResolver.inWindow) || window.OfferResolver.inWindow(o);
      const reg = (window.OfferRegistry || []).filter(
        (o) => o.enabled !== false && inWin(o) && o.trigger && o.trigger.type === 'line_property' && o.trigger.key === '_bundle'
      );
      // A stamp that belongs to ANY known preconfig (even expired/disabled)
      // must never borrow the builder's ladder — an expired NA cart showed
      // "20% off applying..." that could never arm (sweep #4).
      const isPreconfigStamp = stamp && (window.OfferRegistry || []).some(
        (o) => o.trigger && o.trigger.type === 'line_property' && o.trigger.value === stamp
      );
      const offer =
        reg.find((o) => o.trigger.value && o.trigger.value === stamp) || // preconfig, exact (enabled+in-window)
        (isPreconfigStamp ? null : this.builderOffer());                  // generic ONLY for non-preconfig lines
      const tiers = ((offer && offer.reward && offer.reward.tiers) || []).slice().sort((a, b) => (a.qty || 0) - (b.qty || 0));
      // A FIXED (preconfig) bundle has no tier ladder - a single implicit rung from
      // reward.pct + reward.code (the whole set at one percent, no upgrade nudge).
      const fixedRung = !tiers.length && offer && offer.reward && offer.reward.pct
        ? { qty: 0, pct: offer.reward.pct, code: offer.reward.code }
        : null;
      const current = fixedRung ||
        (window.OfferReward && window.OfferReward.tierFor ? window.OfferReward.tierFor(offer, qty) : null);
      const next = fixedRung ? null : (tiers.find((t) => (t.qty || 0) > qty) || null);
      // "applied" is only claimed when the tier's code is ACTUALLY on the cart;
      // earned-but-not-yet-armed shows "applying…" for the sync round-trip.
      const armed = !!(current && current.code && window.OfferReward &&
        window.OfferReward.codeInCart && window.OfferReward.codeInCart(cart, current.code));
      // Codes don't combine: with TWO sets in the cart only one discount can hold.
      // The losing group says so honestly instead of "applying…" forever (H1).
      const managed = (window.OfferReward && window.OfferReward.managedCodes && window.OfferReward.managedCodes()) || {};
      const otherArmed = !armed && !!current &&
        ((cart && cart.discount_codes) || []).some((d) => d && managed[d.code] && (!current.code || d.code !== current.code));

      const mk = (tag, className, text) => {
        const el = document.createElement(tag);
        if (className) el.className = className;
        if (text != null) el.textContent = text; // textContent — copy/config is never markup
        return el;
      };

      const grp = mk('div', 'cart-drawer__set');

      const head = mk('div', 'cart-drawer__set-head');
      const title = mk('b', null, `Your set · ${qty} piece${qty === 1 ? '' : 's'}`);
      const status = mk(
        'span',
        `cart-drawer__set-status${current ? '' : ' is-off'}`,
        current
          ? (armed ? `${current.pct}% off applied`
            : otherArmed ? 'one set discount per order'
            : `${current.pct}% off applying…`)
          : 'no set discount'
      );
      head.appendChild(title);
      head.appendChild(status);
      grp.appendChild(head);

      const body = mk('div', 'cart-drawer__set-body');
      setItems.forEach((item) => body.appendChild(this.createItemElement(item)));
      grp.appendChild(body);

      const foot = mk('div', 'cart-drawer__set-foot');
      let nudge = '';
      if (next) {
        // Unified model (Sean 2026-08-12): ANY eligible lamp counts — PDP, recs,
        // builder — because the collection-scoped code pools them all anyway.
        const need = (next.qty || 0) - qty;
        nudge = current
          ? `Add ${need} more lamp${need === 1 ? '' : 's'} for ${next.pct}% off — any lamp counts`
          : `Add ${need} more lamp${need === 1 ? '' : 's'} to unlock ${next.pct}% off`;
      } else if (current && !fixedRung) {
        nudge = `${current.pct}% off — top tier`;
      } // fixed sets have no ladder — no nudge
      foot.appendChild(mk('span', `cart-drawer__set-nudge${current ? '' : ' is-warn'}`, nudge));
      // "Add to set" (builder sets only — a fixed set isn't extendable): same
      // destination as the data-set-link block, read from it so the cutover URL
      // flip stays single-sourced in the section (Sean, 2026-08-12).
      if (!fixedRung) {
        // URL single-source: read from the section's data-set-link element ONLY.
        // No hardcoded fallback — a wrong-era URL 404s (sweep #4); no element,
        // no link.
        const setLinkEl = this.drawer.querySelector('[data-set-link]');
        if (setLinkEl) {
          const addLink = mk('a', 'cart-drawer__set-add', 'Add to set');
          addLink.setAttribute('href', setLinkEl.getAttribute('href'));
          addLink.setAttribute('data-set-add', '');
          foot.appendChild(addLink);
        }
      }
      const removeBtn = mk('button', 'cart-drawer__set-remove', 'Remove set');
      removeBtn.type = 'button';
      removeBtn.setAttribute('data-remove-set', '');
      removeBtn.addEventListener('click', () => this.removeSet(setItems));
      foot.appendChild(removeBtn);
      grp.appendChild(foot);

      return grp;
    }

    /**
     * Remove every set line in ONE /cart/update.js call (multi-key `updates`).
     * The previous sequential change.js loop fired N rapid requests and never
     * checked responses — this store's per-IP burst limit dropped everything
     * after the first, silently removing only the top line (Henya, 2026-08-11).
     * One request can't partially succeed; on failure the refresh re-syncs the
     * drawer to server truth (set still visibly in the cart — honest).
     */
    async removeSet(setItems) {
      if (this.isRemovingSet) return;
      this.isRemovingSet = true;
      try {
        const updates = {};
        setItems.forEach((item) => { updates[item.key] = 0; });
        const r = await this.fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        });
        if (!r.ok) throw new Error('remove set failed (' + r.status + ')');
      } catch (e) {
        console.warn('[cart] remove set:', e && e.message);
      } finally {
        this.isRemovingSet = false;
        document.dispatchEvent(new Event('cart:refresh'));
      }
    }

    /**
     * Publish the resolved offer object for this cart. Delegates to the pure
     * resolver's publish() (sets window.__activeOffer + fires `offer:resolved`),
     * so the display layer has ONE source. No registry / no resolver ⇒ no-op
     * (behavior unchanged). Called from updateDrawer on every cart change.
     */
    publishActiveOffer(cart) {
      var reg = window.OfferRegistry || [];
      if (!window.OfferResolver || typeof window.OfferResolver.publish !== 'function' || !reg.length) return;
      window.OfferResolver.publish(cart, reg);
    }

    /**
     * Render the "you saved $X" line from the REAL applied discount (cart.total_discount).
     * Not a cosmetic estimate — the offer's code is applied to the cart (syncCartDiscount),
     * so this reflects what checkout will charge. Label = the active offer's badge copy.
     */
    renderOfferSavings(cart) {
      const el = this.drawer.querySelector('[data-cart-discounts]');
      if (!el) return;
      const saved = (cart && cart.total_discount) || 0;
      if (saved > 0) {
        // ATTRIBUTION GUARD: only label the savings with OUR offer's copy when the
        // applicable code is actually one the offer system manages. A store-side
        // automatic (e.g. the legacy "15% OFF 2+ PRODUCTS") also lands in
        // total_discount — labeling that "Set discount applied" would claim money
        // that isn't ours. Automatics retire at cutover; until then: neutral label.
        const managed = (window.OfferReward && window.OfferReward.managedCodes && window.OfferReward.managedCodes()) || {};
        const oursApplied = (cart.discount_codes || []).some((d) => d && d.applicable !== false && managed[d.code]);
        const c = (oursApplied && window.__activeOffer && window.__activeOffer.copy) || {};
        const label = c.unlocked || c.badge || 'Discount';
        el.textContent = '';
        const row = document.createElement('div');
        row.className = 'cart-drawer__discount-item';
        const l = document.createElement('span');
        l.textContent = label; // textContent — metaobject copy is never markup
        const v = document.createElement('span');
        v.textContent = '-' + this.formatMoney(saved);
        row.appendChild(l); row.appendChild(v);
        el.appendChild(row);
        el.style.display = 'block';
      } else {
        el.innerHTML = '';
        el.style.display = 'none';
      }
    }

    /**
     * Handle automatic gift addition/removal based on milestone state
     *
     * Test Cases:
     * 1. No gift → qualifies → gift added
     * 2. Has gift → no longer qualifies → gift removed
     * 3. Tier 1 gift → qualifies for Tier 2 → Tier 1 removed, Tier 2 added
     * 4. Multiple items → all removed → cart empty → gift removed
     * 5. Rapid quantity changes → only one gift added (guard prevents duplicates)
     *
     * @param {Object} cart - Shopify cart object
     * @param {Object} state - Milestone state from tracker
     */
    async handleMilestoneGifts(cart, state) {
      // Guard against concurrent execution
      if (this.isHandlingGifts) {
        console.log('[GIFT] Already handling gifts, skipping');
        return;
      }

      this.isHandlingGifts = true;

      try {
        // Exclude OFFER-derived milestones (isOfferGift) — their gifts are ADDED by
        // OfferReward.syncGift; this legacy engine only acts on settings-configured
        // milestones (which carry product/variant config).
        const giftMilestones = state.completed.filter(m => m.type === 'gift' && !m.isOfferGift);
        // Exclude OFFER-system gifts (stamped _offer_gift) — those are owned by
        // OfferReward.syncGift; the milestone engine must never add/remove them,
        // else the two gift engines fight. (Legacy milestone gifts have _gift_item
        // but no _offer_gift.)
        const currentGifts = cart.items.filter(item =>
          item.properties && item.properties._gift_item === 'true' && !item.properties._offer_gift
        );

        console.log('handleMilestoneGifts:', {
          cartTotal: cart.total_price,
          giftMilestones: giftMilestones.length,
          currentGifts: currentGifts.length
        });

        // Find which gift should be in cart (highest tier)
        let targetGift = giftMilestones[giftMilestones.length - 1];
        // Blackout: an active offer (e.g. a bundle) can suppress the milestone gift
        // so they don't collide — no registry ⇒ no suppression (behavior unchanged).
        if (targetGift && this.isGiftSuppressed(cart)) {
          targetGift = null;
        }

        if (targetGift) {
          // Check if correct gift is in cart
          const hasCorrectGift = currentGifts.some(item =>
            item.variant_id == targetGift.variantId
          );

          console.log('[GIFT] Gift matching check:', {
            targetGiftId: targetGift.id,
            targetVariantId: targetGift.variantId,
            currentGiftVariantIds: currentGifts.map(g => g.variant_id),
            hasCorrectGift: hasCorrectGift
          });

          if (!hasCorrectGift) {
            // Remove wrong gifts IN PARALLEL (not sequential)
            if (currentGifts.length > 0) {
              console.log('[GIFT] Removing wrong gifts:', currentGifts.length);
              const removePromises = currentGifts.map(gift =>
                this.removeGiftFromCart(gift.variant_id)
                  .catch(err => {
                    console.error('[GIFT] Failed to remove gift:', err);
                    return { error: err, variantId: gift.variant_id };
                  })
              );

              // Wait for all removals to complete (or fail)
              await Promise.allSettled(removePromises);
            }

            // Add correct gift
            console.log('[GIFT] Adding correct gift:', targetGift.id);
            await this.addGiftToCart(targetGift);
          }
        } else {
          // No longer qualifies - remove all gifts IN PARALLEL
          if (currentGifts.length > 0) {
            console.log('[GIFT] No target gift, removing all gifts:', currentGifts.length);

            const removePromises = currentGifts.map(gift =>
              this.removeGiftFromCart(gift.variant_id)
                .catch(err => {
                  console.error('[GIFT] Failed to remove gift:', err);
                  return { error: err, variantId: gift.variant_id };
                })
            );

            await Promise.allSettled(removePromises);

            // Refresh cart after removal
            await this.refresh();
          }
        }
      } catch (error) {
        console.error('[GIFT] Error in handleMilestoneGifts:', error);
        // Don't throw - let cart continue working
      } finally {
        // CRITICAL: Always clear the flag
        this.isHandlingGifts = false;
      }
    }

    async addGiftToCart(milestone) {
      try {
        let variantId = milestone.variantId;

        // If variantId is null, fetch product to get first variant
        if (!variantId && milestone.productHandle) {
          try {
            const productResponse = await this.fetch(`/products/${milestone.productHandle}.js`);
            if (productResponse.ok) {
              const product = await productResponse.json();
              if (product.variants && product.variants.length > 0) {
                variantId = product.variants[0].id;
                // CRITICAL FIX: Save fetched variant ID back to milestone object
                // This prevents infinite loop where gift matching fails because variantId is null
                milestone.variantId = variantId;
                console.log('[GIFT] Fetched and saved variant ID:', variantId, 'for milestone:', milestone.id);
              }
            }
          } catch (err) {
            console.error('Error fetching product variants:', err);
            return;
          }
        }

        if (!variantId) {
          console.error('Cannot add gift: no variant ID available');
          return;
        }

        const response = await this.fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: 1,
              properties: {
                _gift_item: 'true',
                _milestone_id: milestone.id,
                _discount_code: milestone.discountCode,
                '_Gift with Purchase': `Free with orders over ${this.formatMoney(milestone.threshold)}`
              }
            }]
          })
        });

        if (response.ok) {
          await this.refresh();
          this.showNotification(`${milestone.qualifiedMessage} added to your cart!`);

          // Track gift addition
          if (window.analytics && typeof window.analytics.track === 'function' && this.lastCartData) {
            try {
              window.analytics.track('Gift Added to Cart', {
                gift_product_id: milestone.productId,
                gift_tier: milestone.id,
                cart_value: this.lastCartData.total_price / 100,
                threshold: milestone.threshold / 100
              });
            } catch (error) {
              console.warn('Analytics tracking failed:', error);
            }
          }
        } else {
          console.error('Failed to add gift to cart');
        }
      } catch (error) {
        console.error('Error adding gift:', error);
      }
    }

    async removeGiftFromCart(variantId) {
      try {
        await this.fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantId.toString(),
            quantity: 0
          })
        });
      } catch (error) {
        console.error('Error removing gift:', error);
      }
    }

    showNotification(message) {
      // Simple notification implementation
      const notification = document.createElement('div');
      notification.className = 'cart-notification';
      notification.textContent = message;
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #108043;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 10000;
        animation: slideIn 0.3s ease;
      `;

      document.body.appendChild(notification);

      setTimeout(() => {
        notification.style.animation = 'slideOut 0.3s ease';
        setTimeout(() => notification.remove(), 300);
      }, 1500); // Reduced from 3000ms for snappier UX
    }

    setupCartNoteHandler() {
      if (this.cartNote && !this.cartNote.dataset.initialized) {
        this.cartNote.dataset.initialized = 'true';
        
        let noteTimeout;
        this.cartNote.addEventListener('input', () => {
          clearTimeout(noteTimeout);
          noteTimeout = setTimeout(() => {
            this.updateCartNote(this.cartNote.value);
          }, 500);
        });
      }
    }
    
    async updateCartNote(note) {
      try {
        await this.fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ note: note })
        });
      } catch (error) {
        console.error('Error updating cart note:', error);
      }
    }
    
    setupTermsHandler() {
      if (this.termsCheckbox && this.checkoutButton && !this.termsCheckbox.dataset.initialized) {
        this.termsCheckbox.dataset.initialized = 'true';
        
        // Check terms before checkout
        this.checkoutButton.addEventListener('click', (e) => {
          if (this.termsCheckbox && !this.termsCheckbox.checked) {
            e.preventDefault();
            alert(window.theme && window.theme.strings && window.theme.strings.cartTermsConfirmation || 'You must agree to the terms and conditions');
            this.termsCheckbox.focus();
          }
        });
      }
    }
    
    /** The GENERIC set offer from the registry (enabled, in-window): either a
     *  collection_qty trigger (the unified any-lamp-counts model) or the legacy
     *  no-value line_property. Preconfig bundles are value-matched, never this. */
    builderOffer() {
      const inWin = (o) => !(window.OfferResolver && window.OfferResolver.inWindow) || window.OfferResolver.inWindow(o);
      return (window.OfferRegistry || []).find(
        (o) => o.enabled !== false && inWin(o) && o.trigger &&
               (o.trigger.type === 'collection_qty' ||
                (o.trigger.type === 'line_property' && o.trigger.key === '_bundle' && !o.trigger.value))
      ) || null;
    }

    /**
     * The builder-link surface (unified model, 2026-08-12). The recs shelf
     * ALWAYS stays — under the unified model a recs add counts toward the tier,
     * so recs are the upsell, not a dead end. Builder set in the cart -> the
     * "Add to your set" link shows above the recs with the next-rung pitch.
     * No set -> recs + a quiet "or build a set" link. All copy derives from
     * the registry tiers (never a hardcoded %); no tiers -> default state.
     * ANY preconfig lines in the cart (blackout session) -> NO builder pitch
     * of either kind — never advertise 15-25% against their 30%.
     */
    updateBuilderLink(cart) {
      const setLink = this.drawer.querySelector('[data-set-link]');
      const builderLink = this.drawer.querySelector('[data-builder-link]');
      if (!setLink && !builderLink) return;
      const title = this.drawer.querySelector('[data-upsell-title]');
      const shelf = this.drawer.querySelector('.cart-drawer__upsell-products');

      const offer = this.builderOffer();
      const tiers = ((offer && offer.reward && offer.reward.tiers) || [])
        .filter((t) => t.qty && t.pct)
        .sort((a, b) => a.qty - b.qty);
      const items = (cart && cart.items) || [];
      const builderQty = (offer && window.OfferReward && window.OfferReward.bundleQty)
        ? window.OfferReward.bundleQty(items, offer)
        : 0;
      const hasPreconfig = items.some((i) => {
        const v = (i.properties || {})._bundle;
        return v && (window.OfferRegistry || []).some(
          (o) => o.trigger && o.trigger.type === 'line_property' && o.trigger.value === v
        );
      });

      // NO builder pitch of any kind in a preconfig (blackout-session) cart —
      // never advertise the 15-25% ladder against their 30% (sweep #4 closed
      // the loose-lamp gap Henya's class of catch predicted).
      const showSetLink = builderQty > 0 && tiers.length > 0 && !hasPreconfig;
      const showQuietLink = !showSetLink && !hasPreconfig && tiers.length > 0 &&
        !window.cartDrawerSettings?.collectionOffer?.enabled;

      if (setLink) {
        setLink.style.display = showSetLink ? 'flex' : 'none';
        const sub = setLink.querySelector('[data-set-link-sub]');
        if (showSetLink && sub) {
          const next = tiers.find((t) => (t.qty || 0) > builderQty) || null;
          const top = tiers[tiers.length - 1];
          sub.textContent = next
            ? `${next.qty - builderQty} more lamp${next.qty - builderQty === 1 ? '' : 's'} unlocks ${next.pct}% off — any lamp counts`
            : `Top tier — keep adding at ${top.pct}% off`;
        }
      }
      // Unified model (2026-08-12): the recs shelf STAYS when a set is in the
      // cart — a recs add now counts toward the tier, so recs are the upsell,
      // not a dead end. (The earlier hiding solved a problem this model deletes.)
      if (title) title.style.display = '';
      if (shelf) shelf.style.display = '';
      if (builderLink) {
        builderLink.style.display = showQuietLink ? '' : 'none';
        if (showQuietLink) {
          builderLink.textContent = `or build a set — ${tiers[0].pct}% off ${tiers[0].qty}+ pieces →`;
        }
      }
    }

    updateBundleMessage(cart) {
      const defaultTitle = this.drawer.querySelector('.cart-drawer__upsell-title--default');
      const bundleTitle = this.drawer.querySelector('.cart-drawer__upsell-title--bundle');
      const bundleDisclaimer = this.drawer.querySelector('[data-bundle-disclaimer]');

      // MULTI-OFFER: this hardcoded "bundle for X% off" pitch is a LEGACY surface with
      // its own drifting % (bundle_discount_percentage) — it collided on camera:
      // advertising 15% while the applied set discount was 20%. Whenever the offer
      // system is present (a resolved __activeOffer exists), this surface YIELDS:
      // the resolved offer's copy + the real savings line (renderOfferSavings) do the
      // messaging; the fake pitch + fake upsell pricing hide. Legacy behavior only
      // when the offer system is absent (backward compat).
      if (window.__activeOffer) {
        if (defaultTitle) defaultTitle.style.display = 'inline';
        if (bundleTitle) bundleTitle.style.display = 'none';
        if (bundleDisclaimer) bundleDisclaimer.style.display = 'none';
        this.updateUpsellPricing(false);
        return;
      }

      // An active collection-quantity offer is exclusive — suppress the
      // bundle upsell message + discount pricing so it can't collide in the drawer.
      // (Belt-and-suspenders alongside the manual "turn bundle off" launch step.)
      if (window.cartDrawerSettings?.collectionOffer?.enabled) {
        if (defaultTitle) defaultTitle.style.display = 'inline';
        if (bundleTitle) bundleTitle.style.display = 'none';
        if (bundleDisclaimer) bundleDisclaimer.style.display = 'none';
        this.updateUpsellPricing(false);
        return;
      }

      if (defaultTitle && bundleTitle) {
        if (cart.item_count >= 1) {
          // Show bundle discount text when cart has items
          defaultTitle.style.display = 'none';
          bundleTitle.style.display = 'inline';
          if (bundleDisclaimer) bundleDisclaimer.style.display = 'block';
          // Update upsell pricing to show discount
          this.updateUpsellPricing(true);
        } else {
          // Show default upsell text when cart is empty
          defaultTitle.style.display = 'inline';
          bundleTitle.style.display = 'none';
          if (bundleDisclaimer) bundleDisclaimer.style.display = 'none';
          // Update upsell pricing to show regular prices
          this.updateUpsellPricing(false);
        }
      }
    }
    
    updateUpsellPricing(showDiscount) {
      const upsellPrices = this.drawer.querySelectorAll('[data-upsell-price]');
      
      upsellPrices.forEach(priceElement => {
        const regularPrice = priceElement.querySelector('[data-regular-price]');
        const discountPrice = priceElement.querySelector('[data-discount-price]');
        const originalPrice = priceElement.querySelector('[data-original-price]');
        
        if (showDiscount && discountPrice && originalPrice) {
          // Show discounted pricing
          if (regularPrice) regularPrice.style.display = 'none';
          discountPrice.style.display = 'inline';
          originalPrice.style.display = 'inline';
        } else if (regularPrice) {
          // Show regular pricing
          regularPrice.style.display = 'inline';
          if (discountPrice) discountPrice.style.display = 'none';
          if (originalPrice) originalPrice.style.display = 'none';
        }
      });
    }
    
    setupUpsellAddToCart() {
      // Capture native fetch for the closure
      const nativeFetch = this.fetch;

      // Handle upsell add to cart buttons with mobile-friendly events
      const handleUpsellClick = async (e) => {
        const addButton = e.target.closest('[data-add-to-cart]');
        if (!addButton) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const variantId = addButton.dataset.variantId;
        if (!variantId) return;

        const originalText = addButton.textContent;
        addButton.textContent = 'Adding...';
        addButton.disabled = true;

        try {
          const response = await nativeFetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [{
                id: variantId,
                quantity: 1
              }]
            })
          });

          if (response.ok) {
            const result = await response.json();
            document.dispatchEvent(new CustomEvent('cart:add', { detail: result }));
            addButton.textContent = 'Added!';
            setTimeout(() => {
              addButton.textContent = originalText;
              addButton.disabled = false;
            }, 2000);
          } else {
            throw new Error('Failed to add to cart');
          }
        } catch (error) {
          console.error('Error adding upsell to cart:', error);
          addButton.textContent = 'Error';
          setTimeout(() => {
            addButton.textContent = originalText;
            addButton.disabled = false;
          }, 2000);
        }
      };

      // Add mobile-friendly event listeners
      if (this.isTouchDevice) {
        this.drawer.addEventListener('touchend', handleUpsellClick, { passive: false });
      }
      this.drawer.addEventListener('click', handleUpsellClick);
    }
    
    setupNoteToggle() {
      const noteToggle = this.drawer.querySelector('[data-note-toggle]');
      const noteWrapper = this.drawer.querySelector('[data-note-wrapper]');
      
      if (noteToggle && noteWrapper) {
        noteToggle.addEventListener('click', () => {
          const isOpen = noteToggle.classList.contains('is-open');
          
          if (isOpen) {
            noteToggle.classList.remove('is-open');
            noteWrapper.style.display = 'none';
          } else {
            noteToggle.classList.add('is-open');
            noteWrapper.style.display = 'block';
            // Focus the textarea when opening
            const textarea = noteWrapper.querySelector('textarea');
            if (textarea) {
              setTimeout(() => textarea.focus(), 100);
            }
          }
        });
      }
    }
    
    async updateInstallments(cart) {
      const installmentsContainer = this.drawer.querySelector('[data-cart-installments]');
      
      if (!installmentsContainer) return;
      
      try {
        // Use Shopify's section rendering API to get updated cart drawer
        const response = await this.fetch(`${window.location.origin}/?sections=cart-drawer`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const sections = await response.json();
          if (sections['cart-drawer']) {
            // Parse the updated section HTML
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = sections['cart-drawer'];
            
            // Find the updated installments container
            const updatedInstallments = tempDiv.querySelector('[data-cart-installments]');
            if (updatedInstallments) {
              // Replace the entire installments container content
              installmentsContainer.innerHTML = updatedInstallments.innerHTML;
              
              // Re-initialize any Shopify payment terms scripts if they exist
              if (window.Shopify && window.Shopify.PaymentTerms && window.Shopify.PaymentTerms.init) {
                setTimeout(() => {
                  try {
                    window.Shopify.PaymentTerms.init();
                  } catch (initError) {
                    console.log('Payment terms init not available or failed:', initError);
                  }
                }, 100);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error updating payment installments:', error);
        
        // Fallback: Try the refresh API if available
        if (window.Shopify && window.Shopify.PaymentTerms && window.Shopify.PaymentTerms.refresh) {
          try {
            window.Shopify.PaymentTerms.refresh();
          } catch (apiError) {
            console.log('Payment terms refresh not available:', apiError);
          }
        }
      }
    }
    
    // Helper method to add mobile-friendly event listeners
    addMobileFriendlyListener(element, callback) {
      if (!element) return;
      
      const handler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        callback(e);
      };
      
      if (this.isTouchDevice) {
        element.addEventListener('touchend', handler, { passive: false });
        // Also add click as fallback
        element.addEventListener('click', handler);
      } else {
        element.addEventListener('click', handler);
      }
    }

    setupCheckoutButton() {
      const checkoutButton = this.drawer.querySelector('[data-checkout-button]');

      if (checkoutButton) {
        // Remove any existing event listeners by cloning the node
        const newButton = checkoutButton.cloneNode(true);
        checkoutButton.parentNode.replaceChild(newButton, checkoutButton);

        // Add mobile-friendly event listener
        this.addMobileFriendlyListener(newButton, (e) => {
          // Check for terms checkbox if it exists
          const termsCheckbox = this.drawer.querySelector('[data-cart-terms]');
          if (termsCheckbox && !termsCheckbox.checked) {
            alert(window.theme && window.theme.strings && window.theme.strings.cartTermsConfirmation || 'You must agree to the terms and conditions');
            termsCheckbox.focus();
            return;
          }

          // Issue the resolved offer's reward at checkout through the ONE
          // applicator (OfferReward): it carries window.__activeOffer.reward.code
          // (or, back-compat, a legacy gift's _discount_code) into
          // /checkout?discount=CODE. A blank code = server-side automatic → plain
          // /checkout. Falls back to the legacy gift path if OfferReward is absent.
          if (window.OfferReward && typeof window.OfferReward.checkoutUrl === 'function') {
            window.location.href = window.OfferReward.checkoutUrl(this.lastCartData);
            return;
          }

          if (this.lastCartData) {
            const giftItem = this.lastCartData.items.find(item =>
              item.properties && item.properties._gift_item === 'true'
            );
            if (giftItem && giftItem.properties._discount_code) {
              window.location.href = `/checkout?discount=${giftItem.properties._discount_code}`;
              return;
            }
          }
          window.location.href = '/checkout';
        });
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.cartDrawer = new CartDrawer();
      // Check if we should auto-open the drawer (from /cart redirect)
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('open_cart') === 'true') {
        window.cartDrawer.open();
        // Clean up URL parameter
        urlParams.delete('open_cart');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);
      }
    });
  } else {
    window.cartDrawer = new CartDrawer();
    // Check if we should auto-open the drawer (from /cart redirect)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('open_cart') === 'true') {
      window.cartDrawer.open();
      // Clean up URL parameter
      urlParams.delete('open_cart');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }
  }

  // Hook into existing cart systems but prevent old popup
  const originalOnItemAdded = window.Shopify && window.Shopify.onItemAdded;
  if (window.Shopify) {
    window.Shopify.onItemAdded = function(item) {
      // Don't call original handler to prevent old popup
      // if (originalOnItemAdded) {
      //   originalOnItemAdded(item);
      // }
      document.dispatchEvent(new CustomEvent('cart:add', { detail: item }));
    };
  }

  // Hook into theme cart if it exists
  if (window.theme && window.theme.cart) {
    const originalFetchTotals = window.theme.cart.fetchTotals;
    if (originalFetchTotals) {
      window.theme.cart.fetchTotals = function() {
        originalFetchTotals.call(this);
        document.dispatchEvent(new Event('cart:refresh'));
      };
    }
  }
})();